import { useState, useEffect } from 'react';
import { apiService } from './services/api';
import { authService } from './services/auth';
import { ResponseItem, UseCaseGroup } from './types';
import Login from './components/Login';
import AppHeader from './components/AppHeader';
import UseCaseDisplay from './components/UseCaseDisplay';
import InfoDisplay from './components/InfoDisplay';
import GroupSelector from './components/GroupSelector';
import ResponseItems from './components/ResponseItems';
import { FiDownload, FiTrash2 } from 'react-icons/fi';
import {
  Card,
  Container,
  AppBackground,
  ProcessButton,
  LoadingSpinner,
  ErrorMessage,
  InputGroup,
  Label,
  TextInput,
  BottomNavigation,
  BottomNavButton,
  SessionList,
  SessionItem,
  DeleteSessionButton,
  ConfirmDialog,
  ConfirmDialogOverlay,
  ConfirmDialogActions,
} from './components/StyledComponents';
import { MainLayout, ContentArea } from './components/StyledComponents';

const OPTIONAL_CATEGORIES = ['Verbesserung', 'Unterstützend'];

function App() {
  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(false);
  const [authLoading, setAuthLoading] = useState<boolean>(true);


  const [savedSessions, setSavedSessions] = useState<{ confluence_id: string; state: string; updated_at: string }[]>([]);
  const [confirmDelete, setConfirmDelete] = useState<string | null>(null);
  
  const [sessionId, setSessionId] = useState<string>('');
  const [useCaseGroups, setUseCaseGroups] = useState<Record<string, UseCaseGroup>>({});
  const [currentGroupKey, setCurrentGroupKey] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(0);
  const [useCasesLoaded, setUseCasesLoaded] = useState<boolean>(false);

  // Check authentication status on app load
  useEffect(() => {
    const checkAuth = () => {
      const authenticated = authService.isAuthenticated();
      setIsAuthenticated(authenticated);
      setAuthLoading(false);
    };

    checkAuth();
  }, []);

  const handleLoginSuccess = () => {
    setIsAuthenticated(true);
  };

  const loadSavedSessions = async () => {
    try {
      const sessions = await apiService.listSessions();
      setSavedSessions(sessions);
    } catch {
      // non-critical, fail silently
    }
  };

  const handleDeleteSession = async () => {
    if (!confirmDelete) return;
    try {
      await apiService.deleteSession(confirmDelete);
      await loadSavedSessions();
    } catch {
      setError('Sitzung konnte nicht gelöscht werden. Bitte versuchen Sie es erneut.');
    } finally {
      setConfirmDelete(null);
    }
  };

  useEffect(() => {
    if (isAuthenticated) {
      loadSavedSessions();
    }
  }, [isAuthenticated]);

  // Derived state for the current use case
  const currentGroup = currentGroupKey ? useCaseGroups[currentGroupKey] : null;
  const useCase = currentGroup?.use_cases[currentPage];

  const [responseItems, setResponseItems] = useState<ResponseItem[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string>('');
  const [processing, setProcessing] = useState<boolean>(false);
  const [processedCache, setProcessedCache] = useState<Record<string, { responseItems: ResponseItem[] }>>({});
  const [manualTexts, setManualTexts] = useState<Record<string, string>>({});
  const [isNavigationLocked, setIsNavigationLocked] = useState<boolean>(false);
  const [isDownloading, setIsDownloading] = useState<boolean>(false);
  const [uploadedManuals, setUploadedManuals] = useState<string[]>([]);
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [loadingStep, setLoadingStep] = useState<string>('');

  // Compute the target of the prev navigation (may cross group boundaries)
  const prevNavTarget = (() => {
    if (!currentGroupKey || !useCaseGroups[currentGroupKey]) return null;
    if (currentPage > 0) return { groupKey: currentGroupKey, page: currentPage - 1 };
    const groupKeys = Object.keys(useCaseGroups);
    const idx = groupKeys.indexOf(currentGroupKey);
    if (idx > 0) {
      const prevKey = groupKeys[idx - 1];
      return { groupKey: prevKey, page: useCaseGroups[prevKey].use_cases.length - 1 };
    }
    return null;
  })();
  const canGoBackToPrev = prevNavTarget !== null && !!processedCache[`${prevNavTarget.groupKey}-${prevNavTarget.page}`];

  // Page completion statuses for the current group's dot indicators
  const pageStatuses: ('unstarted' | 'partial' | 'done')[] = currentGroup
    ? currentGroup.use_cases.map((_, i) => {
        const cached = processedCache[`${currentGroupKey}-${i}`];
        if (!cached) return 'unstarted';
        if (cached.responseItems.every(item => item.isConfirmed)) return 'done';
        return 'partial';
      })
    : [];

  // When the session ID field changes, reset upload state and fetch already-uploaded
  // manuals if this ID matches a known saved session.
  useEffect(() => {
    setUploadedManuals([]);
    setSelectedFiles([]);
    if (!sessionId) return;
    apiService.getUploadedManuals(sessionId)
      .then(({ uploaded }) => setUploadedManuals(uploaded))
      .catch(() => {});
  }, [sessionId]);

  // Persist processedCache to localStorage whenever it changes
  useEffect(() => {
    if (sessionId && useCasesLoaded) {
      localStorage.setItem(`needle-cache-${sessionId}`, JSON.stringify(processedCache));
    }
  }, [processedCache, sessionId, useCasesLoaded]);

  // Persist current position so we can resume on reload
  useEffect(() => {
    if (sessionId && useCasesLoaded && currentGroupKey !== null) {
      localStorage.setItem(`needle-pos-${sessionId}`, JSON.stringify({ groupKey: currentGroupKey, page: currentPage }));
    }
  }, [currentGroupKey, currentPage, sessionId, useCasesLoaded]);

  // Helper function to get tooltip text for navigation
  const getNavigationTooltip = () => {
    if (isNavigationLocked) {
      return "Bitte verarbeiten Sie alle Änderungsvorschläge, bevor Sie fortfahren.";
    }
    return undefined;
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    e.target.value = '';
    setSelectedFiles(files);
    setError('');
  };

  const handleDownloadManual = async () => {
    if (!currentGroupKey || isDownloading) return;
    
    setIsDownloading(true);
    try {
      const blob = await apiService.downloadManual(currentGroupKey);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `Handbuch_${currentGroupKey}.docx`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error downloading manual:', error);
      alert('Failed to download manual. Please try again.');
    } finally {
      setIsDownloading(false);
    }
  };

  const handleItemConfirmed = (itemIndex: number, updatedManualText: string, appliedText: string) => {
    if (!currentGroupKey) return;
    
    // Update the manual text for the current group
    setManualTexts(prev => {
      const newManualTexts = { ...prev, [currentGroupKey]: updatedManualText };
      return newManualTexts;
    });
    
    // Update the response item to mark it as confirmed
    setResponseItems(prev => {
      const updated = prev.map((item, index) => 
        index === itemIndex
          ? { ...item, isConfirmed: true, updated_text: appliedText, lastAppliedText: appliedText }
          : item
      );
      
      // Check if all mandatory items are confirmed to unlock navigation
      const allConfirmed = updated.every(item => item.isConfirmed || item.isRejected || OPTIONAL_CATEGORIES.includes(item.category));
      if (allConfirmed) {
        setIsNavigationLocked(false);
      }
      
      // Update the cache with the confirmed items
      const pageId = `${currentGroupKey}-${currentPage}`;
      setProcessedCache(cache => {
        const newCache = { ...cache, [pageId]: { responseItems: updated } };
        return newCache;
      });
      
      return updated;
    });
  };

  const handleItemUnconfirmed = async (itemIndex: number) => {
    if (!currentGroupKey) return;

    const item = responseItems[itemIndex];
    if (!item) return;

    const revertFromText = item.lastAppliedText || item.updated_text;
    const revertToText = item.original_text;

    try {
      const response = await apiService.confirmChange(
        false,
        revertFromText,
        revertToText,
        currentGroupKey,
        currentPage,
        itemIndex,
        revertToText
      );

      setManualTexts(prev => ({
        ...prev,
        [currentGroupKey]: response.manual_text
      }));
    } catch (error) {
      console.error('Error reverting confirmed change:', error);
      setError('Fehler beim Rückgängigmachen der bestätigten Änderung.');
      return;
    }
    
    // Update the response item to mark it as unconfirmed
    setResponseItems(prev => {
      const updated = prev.map((item, index) => 
        index === itemIndex ? { ...item, isConfirmed: false, lastAppliedText: undefined } : item
      );
      
      // Lock navigation only if a mandatory item was unconfirmed
      const hasMandatoryUnconfirmed = updated.some(item => !item.isConfirmed && !item.isRejected && !OPTIONAL_CATEGORIES.includes(item.category));
      setIsNavigationLocked(hasMandatoryUnconfirmed);
      
      // Update the cache with the updated items
      const pageId = `${currentGroupKey}-${currentPage}`;
      setProcessedCache(cache => {
        const newCache = { ...cache, [pageId]: { responseItems: updated } };
        return newCache;
      });
      
      return updated;
    });
  };

  const handleItemRejected = (itemIndex: number) => {
    if (!currentGroupKey) return;

    setResponseItems(prev => {
      const updated = prev.map((item, index) =>
        index === itemIndex
          ? { ...item, isRejected: !item.isRejected }
          : item
      );

      const hasMandatoryUnresolved = updated.some(
        item => !item.isConfirmed && !item.isRejected && !OPTIONAL_CATEGORIES.includes(item.category)
      );
      setIsNavigationLocked(hasMandatoryUnresolved);

      const pageId = `${currentGroupKey}-${currentPage}`;
      setProcessedCache(cache => ({ ...cache, [pageId]: { responseItems: updated } }));

      return updated;
    });
  };

  const handleSelectGroup = (groupKey: string) => {
    if (groupKey !== currentGroupKey && !isNavigationLocked) {
      navigateToPage(groupKey, 0);
    }
  };

  const loadCurrentManualText = async (groupKey: string) => {
    try {
      const response = await apiService.getCurrentManualText(groupKey);
      setManualTexts(prev => {
        const newManualTexts = { ...prev, [groupKey]: response.manual_text };
        return newManualTexts;
      });
    } catch (error) {
      console.error('Error loading current manual text:', error);
      // Fallback to original manual text if current text isn't available
      if (useCaseGroups[groupKey]) {
        setManualTexts(prev => ({ ...prev, [groupKey]: useCaseGroups[groupKey].manual_text }));
      }
    }
  };

  const navigateToPage = async (newGroupKey: string, newPage: number) => {
    const pageId = `${newGroupKey}-${newPage}`;
    
    // Restore cached response items if they exist
    if (processedCache[pageId]) {
      setResponseItems(processedCache[pageId].responseItems);
      // Check if navigation should be locked for this page
      const hasUnconfirmedItems = processedCache[pageId].responseItems.some(item => !item.isConfirmed && !item.isRejected && !OPTIONAL_CATEGORIES.includes(item.category));
      setIsNavigationLocked(hasUnconfirmedItems);
    } else {
      // Try to load proposals from the DB for this page
      try {
        const { proposals } = await apiService.getProposals(newGroupKey, newPage);
        if (proposals && proposals.length > 0) {
          const itemsFromDb = proposals.map((p: any, idx: number) => ({
            category: p.category,
            location: p.location,
            context: p.context,
            original_text: p.original_text,
            updated_text: p.confirmed_text ?? p.updated_text,
            reasoning: p.reasoning,
            id: `${newGroupKey}-${newPage}-${idx}`,
            isConfirmed: !!p.is_confirmed,
            lastAppliedText: p.confirmed_text ?? undefined,
          }));
          setResponseItems(itemsFromDb);
          setProcessedCache(prev => ({ ...prev, [pageId]: { responseItems: itemsFromDb } }));
          const hasUnconfirmed = itemsFromDb.some((item: any) => !item.isConfirmed && !item.isRejected && !OPTIONAL_CATEGORIES.includes(item.category));
          setIsNavigationLocked(hasUnconfirmed);
        } else {
          setResponseItems([]);
          setIsNavigationLocked(false);
        }
      } catch {
        setResponseItems([]);
        setIsNavigationLocked(false);
      }
    }

    // Load manual text for new group if we don't have it
    if (!manualTexts[newGroupKey]) {
      loadCurrentManualText(newGroupKey);
    }

    setCurrentGroupKey(newGroupKey);
    setCurrentPage(newPage);
  };

  const handlePrev = () => {
    if (!currentGroupKey || !useCaseGroups[currentGroupKey]) return;
    if (isNavigationLocked && !canGoBackToPrev) return;

    let newGroupKey = currentGroupKey;
    let newPage = currentPage;

    if (currentPage > 0) {
      newPage = currentPage - 1;
    } else {
      const groupKeys = Object.keys(useCaseGroups);
      const currentGroupIndex = groupKeys.indexOf(currentGroupKey);
      if (currentGroupIndex > 0) {
        newGroupKey = groupKeys[currentGroupIndex - 1];
        newPage = useCaseGroups[newGroupKey].use_cases.length - 1;
      } else {
        return;
      }
    }

    navigateToPage(newGroupKey, newPage);
  };

  const handleNext = () => {
    if (!currentGroupKey || !useCaseGroups[currentGroupKey] || isNavigationLocked) return;

    let newGroupKey = currentGroupKey;
    let newPage = currentPage;

    if (currentPage < useCaseGroups[currentGroupKey].use_cases.length - 1) {
      newPage = currentPage + 1;
    } else {
      const groupKeys = Object.keys(useCaseGroups);
      const currentGroupIndex = groupKeys.indexOf(currentGroupKey);
      if (currentGroupIndex < groupKeys.length - 1) {
        newGroupKey = groupKeys[currentGroupIndex + 1];
        newPage = 0;
      }
    }

    navigateToPage(newGroupKey, newPage);
  };

  const loadAllUseCases = async (idOverride?: string) => {
    const idToLoad = idOverride ?? sessionId;
    if (!idToLoad) {
      setError('Bitte geben Sie eine Confluence Parent ID ein, unter der sich die Use Cases befinden.');
      return;
    }
    if (idOverride) setSessionId(idOverride);
    setLoading(true);
    setLoadingStep('');
    setError('');
    setResponseItems([]);
    setUseCaseGroups({});
    setCurrentGroupKey(null);
    setCurrentPage(0);
    setProcessedCache({});
    setManualTexts({});
    setIsNavigationLocked(false);

    try {
      // Upload selected files first (only when loading a manually-entered ID, not a saved session)
      if (!idOverride && selectedFiles.length > 0) {
        for (let i = 0; i < selectedFiles.length; i++) {
          const file = selectedFiles[i];
          setLoadingStep(`Handbuch „${file.name}" wird extrahiert… (${i + 1}/${selectedFiles.length})`);
          try {
            const { manual_type } = await apiService.uploadManual(idToLoad, file);
            setUploadedManuals(prev => prev.includes(manual_type) ? prev : [...prev, manual_type]);
          } catch (err: any) {
            throw new Error(err.response?.data?.detail || `Fehler beim Verarbeiten von „${file.name}"`);
          }
        }
        setSelectedFiles([]);
      }

      setLoadingStep('Use Cases werden von Confluence geladen…');
      const data = await apiService.getAllUseCases(idToLoad);
      setUseCaseGroups(data);

      // Determine start position: use saved position if valid, otherwise first group
      const firstGroupKey = Object.keys(data)[0];
      let startGroupKey = firstGroupKey;
      let startPage = 0;
      const savedPos = localStorage.getItem(`needle-pos-${idToLoad}`);
      if (savedPos) {
        try {
          const pos = JSON.parse(savedPos);
          if (pos.groupKey && data[pos.groupKey] && pos.page < data[pos.groupKey].use_cases.length) {
            startGroupKey = pos.groupKey;
            startPage = pos.page;
          }
        } catch {
          localStorage.removeItem(`needle-pos-${idToLoad}`);
        }
      }

      if (startGroupKey) {
        setCurrentGroupKey(startGroupKey);
        setCurrentPage(startPage);
        loadCurrentManualText(startGroupKey);
      }
      setUseCasesLoaded(true);

      // Restore processedCache from localStorage if available for this session
      const savedCache = localStorage.getItem(`needle-cache-${idToLoad}`);
      if (savedCache) {
        try {
          const restored = JSON.parse(savedCache);
          setProcessedCache(restored);
          // Restore responseItems for the start page from localStorage
          const startPageId = `${startGroupKey}-${startPage}`;
          if (restored[startPageId]) {
            setResponseItems(restored[startPageId].responseItems);
            const hasUnconfirmed = restored[startPageId].responseItems.some((item: ResponseItem) => !item.isConfirmed && !item.isRejected && !OPTIONAL_CATEGORIES.includes(item.category));
            setIsNavigationLocked(hasUnconfirmed);
          } else {
            // No localStorage cache for this page — try DB
            try {
              const { proposals } = await apiService.getProposals(startGroupKey, startPage);
              if (proposals && proposals.length > 0) {
                const itemsFromDb = proposals.map((p: any, idx: number) => ({
                  category: p.category,
                  location: p.location,
                  context: p.context,
                  original_text: p.original_text,
                  updated_text: p.confirmed_text ?? p.updated_text,
                  reasoning: p.reasoning,
                  id: `${startGroupKey}-${startPage}-${idx}`,
                  isConfirmed: !!p.is_confirmed,
                  lastAppliedText: p.confirmed_text ?? undefined,
                }));
                setResponseItems(itemsFromDb);
                setProcessedCache(prev => ({ ...prev, [startPageId]: { responseItems: itemsFromDb } }));
                const hasUnconfirmed = itemsFromDb.some((item: any) => !item.isConfirmed && !item.isRejected && !OPTIONAL_CATEGORIES.includes(item.category));
                setIsNavigationLocked(hasUnconfirmed);
              }
            } catch { /* non-critical */ }
          }
        } catch {
          localStorage.removeItem(`needle-cache-${idToLoad}`);
        }
      } else {
        // No localStorage cache at all — try DB for the start page
        try {
          const { proposals } = await apiService.getProposals(startGroupKey, startPage);
          if (proposals && proposals.length > 0) {
            const startPageId = `${startGroupKey}-${startPage}`;
            const itemsFromDb = proposals.map((p: any, idx: number) => ({
              category: p.category,
              location: p.location,
              context: p.context,
              original_text: p.original_text,
              updated_text: p.confirmed_text ?? p.updated_text,
              reasoning: p.reasoning,
              id: `${startGroupKey}-${startPage}-${idx}`,
              isConfirmed: !!p.is_confirmed,
              lastAppliedText: p.confirmed_text ?? undefined,
            }));
            setResponseItems(itemsFromDb);
            setProcessedCache(prev => ({ ...prev, [startPageId]: { responseItems: itemsFromDb } }));
            const hasUnconfirmed = itemsFromDb.some((item: any) => !item.isConfirmed && !item.isRejected && !OPTIONAL_CATEGORIES.includes(item.category));
            setIsNavigationLocked(hasUnconfirmed);
          }
        } catch { /* non-critical */ }
      }
    } catch (err: any) {
      const msg = err.message || 'Laden fehlgeschlagen. Bitte Backend-Verbindung und Session-ID prüfen.';
      setError(msg);
      console.error('Error loading use cases:', err);
    } finally {
      setLoading(false);
      setLoadingStep('');
    }
  };

  const handleNewProcessing = () => {
    const confirmed = window.confirm('Sind Sie sich sicher? Ihre Eingaben gehen dann verloren.');
    if (confirmed) {
      // Reset all state
      setUseCasesLoaded(false);
      setSessionId('');
      setUseCaseGroups({});
      setCurrentGroupKey(null);
      setCurrentPage(0);
      setResponseItems([]);
      setProcessedCache({});
      setManualTexts({});
      setIsNavigationLocked(false);
      setError('');
      setUploadedManuals([]);
      setSelectedFiles([]);
      setLoadingStep('');
      localStorage.removeItem(`needle-cache-${sessionId}`);
      localStorage.removeItem(`needle-pos-${sessionId}`);
    }
  };

  const handleProcess = async () => {
    if (!useCase || !currentGroupKey) return;
    setProcessing(true);
    setError('');
    setIsNavigationLocked(true); // Lock navigation when processing starts
    try {
      const data = await apiService.processUseCase(useCase.text, currentGroupKey, currentPage);
      // Add unique IDs to response items for better tracking
      const itemsWithIds = (data.response_items || []).map((item, index) => ({
        ...item,
        id: `${currentGroupKey}-${currentPage}-${index}`,
        isConfirmed: false
      }));
      
      setResponseItems(itemsWithIds);
      
      // Cache the processed items immediately
      const pageId = `${currentGroupKey}-${currentPage}`;
      setProcessedCache(prev => ({ ...prev, [pageId]: { responseItems: itemsWithIds } }));

    } catch (err) {
      setError('Fehler beim Verarbeiten des Use Cases. Bitte versuchen Sie es erneut.');
      console.error('Error processing use case:', err);
      setIsNavigationLocked(false); // Unlock navigation on error
    } finally {
      setProcessing(false);
    }
  };

  return (
    <>
      {authLoading ? (
        <Container>
          <LoadingSpinner />
        </Container>
      ) : !isAuthenticated ? (
        <Login onLoginSuccess={handleLoginSuccess} />
      ) : (
        <AppBackground>
          <AppHeader />
          <MainLayout>
            <ContentArea>
              <Container>
                <>
                    {!useCasesLoaded ? (
                      <Card>
                        <InputGroup>
                          <Label htmlFor="sessionId">Confluence Parent ID</Label>
                          <TextInput
                            id="sessionId"
                            type="text"
                            value={sessionId}
                            onChange={(e) => setSessionId(e.target.value)}
                            placeholder="Bitte geben Sie eine Confluence Parent ID ein, unter der sich die Use Cases befinden."
                          />
                        </InputGroup>
                        {sessionId && (
                          <InputGroup>
                            <Label>Handbücher auswählen (.docx)</Label>
                            <p style={{ fontSize: '0.8rem', color: '#6b7280', margin: '0 0 0.75rem' }}>
                              Dateinamen müssen mit <strong>Betreibende</strong>, <strong>UntM</strong>, <strong>ZuwSt</strong> oder <strong>Admin</strong> beginnen.
                              Extraktion und Upload erfolgen beim Klick auf „Use Cases Laden".
                              Ohne Auswahl werden die zuletzt hinterlegten Handbücher verwendet.
                            </p>
                            <label style={{
                              display: 'inline-flex', alignItems: 'center', gap: '0.5rem',
                              padding: '0.5rem 1rem', border: '1px solid #e5e7eb',
                              borderRadius: '8px', cursor: loading ? 'not-allowed' : 'pointer',
                              fontSize: '0.9rem', color: '#374151', background: loading ? '#f9fafb' : 'white',
                            }}>
                              <input
                                type="file"
                                accept=".docx"
                                multiple
                                style={{ display: 'none' }}
                                onChange={handleFileSelect}
                                disabled={loading}
                              />
                              + Handbücher auswählen
                            </label>
                            {selectedFiles.length > 0 && (
                              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem', marginTop: '0.75rem' }}>
                                {selectedFiles.map(f => (
                                  <span key={f.name} style={{
                                    display: 'inline-flex', alignItems: 'center', gap: '4px',
                                    background: '#f3f4f6', color: '#374151',
                                    border: '1px solid #e5e7eb',
                                    borderRadius: '999px', padding: '2px 10px', fontSize: '0.8rem',
                                  }}>
                                    {f.name}
                                  </span>
                                ))}
                              </div>
                            )}
                            {uploadedManuals.length > 0 && (
                              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem', marginTop: '0.5rem' }}>
                                {uploadedManuals.map(type => (
                                  <span key={type} style={{
                                    display: 'inline-flex', alignItems: 'center', gap: '4px',
                                    background: 'rgba(0,159,227,0.1)', color: '#003A70',
                                    border: '1px solid rgba(0,159,227,0.3)',
                                    borderRadius: '999px', padding: '2px 10px', fontSize: '0.8rem', fontWeight: 600,
                                  }}>
                                    ✓ {type}
                                  </span>
                                ))}
                              </div>
                            )}
                          </InputGroup>
                        )}
                        <ProcessButton onClick={() => loadAllUseCases()} disabled={loading || !sessionId}>
                          Use Cases Laden
                        </ProcessButton>
                        {loading && loadingStep && (
                          <div style={{
                            marginTop: '1rem', padding: '0.75rem 1rem',
                            background: 'rgba(0,159,227,0.06)', border: '1px solid rgba(0,159,227,0.2)',
                            borderRadius: '8px', display: 'flex', alignItems: 'center', gap: '0.75rem',
                            fontSize: '0.875rem', color: '#003A70',
                          }}>
                            <LoadingSpinner style={{ width: '18px', height: '18px', margin: 0, borderWidth: '2px', flexShrink: 0 }} />
                            {loadingStep}
                          </div>
                        )}
                        {savedSessions.length > 0 && (
                          <>
                            <Label style={{ marginTop: '1.5rem', display: 'block' }}>Gespeicherte Sitzungen</Label>
                            <SessionList>
                              {savedSessions.map((s) => (
                                <SessionItem
                                  key={s.confluence_id}
                                  onClick={() => loadAllUseCases(s.confluence_id)}
                                >
                                  <span style={{ fontWeight: 600 }}>{s.confluence_id}</span>
                                  <span style={{ fontSize: '0.8rem', color: '#9ca3af' }}>
                                    {new Date(s.updated_at).toLocaleString('de-DE')}
                                  </span>
                                  <DeleteSessionButton
                                    title="Sitzung löschen"
                                    onClick={(e) => { e.stopPropagation(); setConfirmDelete(s.confluence_id); }}
                                  >
                                    <FiTrash2 size={14} />
                                  </DeleteSessionButton>
                                </SessionItem>
                              ))}
                            </SessionList>
                          </>
                        )}
                      </Card>
                    ) : (
                      <div style={{ textAlign: 'center', marginBottom: '2rem' }}>
                        <ProcessButton onClick={handleNewProcessing} style={{ display: 'inline-flex', alignItems: 'center', gap: '0.5rem' }}>
                          <span style={{ fontSize: '1.3rem' }}>↻</span>
                          Neue Verarbeitung starten
                        </ProcessButton>
                      </div>
                    )}
                    
                    {error && <ErrorMessage>{error}</ErrorMessage>}
                    
                    {loading && <LoadingSpinner />}

                    <GroupSelector
                      groupKeys={Object.keys(useCaseGroups)}
                      currentGroupKey={currentGroupKey}
                      onSelectGroup={handleSelectGroup}
                      isNavigationLocked={isNavigationLocked}
                      tooltipText={getNavigationTooltip()}
                    />

                    {useCase && currentGroup && (
                      <>
                        <InfoDisplay
                          label="Handbuch Typ"
                          value={currentGroupKey}
                          currentManualText={currentGroupKey ? manualTexts[currentGroupKey] : undefined}
                        />
                        <UseCaseDisplay 
                          useCase={useCase.text}
                          currentPage={currentPage}
                          totalPages={currentGroup.use_cases.length}
                          onPrev={handlePrev}
                          onNext={handleNext}
                          onNavigateToPage={(page) => navigateToPage(currentGroupKey!, page)}
                          isNavigationLocked={isNavigationLocked}
                          canGoBackToPrev={canGoBackToPrev}
                          tooltipText={getNavigationTooltip()}
                          pageStatuses={pageStatuses}
                        />
                        {!processedCache[`${currentGroupKey}-${currentPage}`] && (
                          <ProcessButton 
                            onClick={handleProcess} 
                            disabled={processing}
                          >
                            {processing ? 'Verarbeite...' : 'Use Case Verarbeiten'}
                          </ProcessButton>
                        )}
                      </>
                    )}
                    
                    {processing && !responseItems.length && <LoadingSpinner />}
                    
                    {responseItems.length > 0 && currentGroupKey && (
                      <ResponseItems 
                        responseItems={responseItems} 
                        currentManualType={currentGroupKey}
                        useCaseIdx={currentPage}
                        onItemConfirmed={handleItemConfirmed}
                        onItemUnconfirmed={handleItemUnconfirmed}
                        onItemRejected={handleItemRejected}
                        currentManualText={currentGroupKey ? manualTexts[currentGroupKey] : undefined}
                      />
                    )}

                    {useCase && currentGroup && responseItems.length > 0 && (
                      <BottomNavigation>
                        <BottomNavButton
                          onClick={handlePrev}
                          disabled={!canGoBackToPrev && (currentPage === 0 || isNavigationLocked)}
                          variant="secondary"
                        >
                          ← Vorheriger Use Case
                        </BottomNavButton>
                        
                        <BottomNavButton
                          onClick={handleDownloadManual}
                          disabled={!currentGroupKey || isDownloading}
                          variant="secondary"
                        >
                          {isDownloading ? (
                            <>
                              <LoadingSpinner style={{ width: '20px', height: '20px', margin: 0 }} />
                              Lädt...
                            </>
                          ) : (
                            <>
                              <FiDownload />
                              Zwischenergebnis herunterladen
                            </>
                          )}
                        </BottomNavButton>
                        
                        <BottomNavButton
                          onClick={handleNext}
                          disabled={currentPage >= currentGroup.use_cases.length - 1 || isNavigationLocked}
                          variant="primary"
                        >
                          Nächster Use Case →
                        </BottomNavButton>
                      </BottomNavigation>
                    )}
                </>
              </Container>
            </ContentArea>
          </MainLayout>
        </AppBackground>
      )}
      {confirmDelete && (
        <ConfirmDialogOverlay onClick={() => setConfirmDelete(null)}>
          <ConfirmDialog onClick={(e) => e.stopPropagation()}>
            <p style={{ margin: '0 0 1.2rem', fontWeight: 600, fontSize: '1rem' }}>Sitzung löschen?</p>
            <p style={{ margin: '0 0 1.5rem', fontSize: '0.875rem', color: '#6b7280' }}>
              Die Sitzung <strong>{confirmDelete}</strong> wird unwiderruflich gelöscht.
            </p>
            <ConfirmDialogActions>
              <button onClick={() => setConfirmDelete(null)}>Abbrechen</button>
              <button className="danger" onClick={handleDeleteSession}>Löschen</button>
            </ConfirmDialogActions>
          </ConfirmDialog>
        </ConfirmDialogOverlay>
      )}
    </>
  );
}

export default App;
